import { useState } from 'react';
import { ServiceProcedure } from '../../types/companyResourceTypes';
import { findExistingCategoryName, nextCategorySortOrder, procedureCategoryName } from '../../utils/companyResourceUtils';
import { createServiceProcedure, updateServiceProcedure, listServiceProcedureConsumablesByProcedureId, createServiceProcedureConsumable } from '../../graphql/companyResourceQueries';
import { useUserContext } from '../../contexts/UserContext';
import { CustomModal } from '../CustomModal';
import { Btn, InputField, SearchSuggest } from '../ui';

interface ServiceProcedureModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (saved?: ServiceProcedure) => Promise<void>;
    editItem: ServiceProcedure | null;
    isCopy?: boolean;
    /** Prefill procedure name when creating from Daily Service Form Builder. */
    initialName?: string;
    /** Join count on the source procedure when copying. Catalog items are not duplicated. */
    linkedConsumableCount?: number;
    categories: string[];
    allItems?: ServiceProcedure[];
    companyId: string;
    /** Prefill category when adding from inside a procedure category. */
    defaultCategory?: string;
}

export const ServiceProcedureModal = ({
    isOpen,
    onClose,
    onSave,
    editItem,
    isCopy = false,
    initialName = '',
    linkedConsumableCount = 0,
    categories,
    allItems = [],
    companyId,
    defaultCategory = '',
}: ServiceProcedureModalProps) => {
    const { companyUser, companyOwner } = useUserContext();
    const currentUserId = companyUser?.id || companyOwner?.id || '';
    const currentUserName = companyUser?.name || companyOwner?.name || '';

    const [category, setCategory] = useState(editItem?.category || defaultCategory || '');
    const [newCategory, setNewCategory] = useState('');
    const [itemName, setItemName] = useState(editItem?.itemName || initialName);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    const categoryValue = category === '__new__' ? newCategory : category;
    const categoryOptions = categories.map((c) => ({ value: c, label: c }));

    const handleCategoryChange = (val: string) => {
        const exactMatch = categories.find((c) => c.toLowerCase() === val.toLowerCase());
        if (exactMatch) {
            setCategory(exactMatch);
            setNewCategory('');
        } else if (val.trim()) {
            setCategory('__new__');
            setNewCategory(val.trim());
        } else {
            setCategory('');
            setNewCategory('');
        }
    };

    const validate = () => {
        const errs: Record<string, string> = {};
        const finalCat = (category === '__new__' ? newCategory : category).trim();
        const finalName = itemName.trim();
        if (!finalCat) errs.category = 'Category is required';
        if (!finalName) errs.itemName = 'Procedure is required';
        if (finalCat && finalName) {
            const duplicate = allItems.find((item) => {
                if (editItem && !isCopy && item.id === editItem.id) return false;
                return (
                    item.category.trim().toLowerCase() === finalCat.toLowerCase() &&
                    item.itemName.trim().toLowerCase() === finalName.toLowerCase()
                );
            });
            if (duplicate) {
                errs._form = 'A procedure with this name already exists in this category.';
            }
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            let finalCategory = (category === '__new__' ? newCategory : category).trim();
            const existingCat = findExistingCategoryName(categories, finalCategory);
            if (existingCat) finalCategory = existingCat;

            const categoryChanged = !editItem || isCopy || procedureCategoryName(editItem) !== finalCategory;
            const categorySortOrder = categoryChanged
                ? nextCategorySortOrder(
                      editItem && !isCopy ? allItems.filter((item) => item.id !== editItem.id) : allItems,
                      finalCategory,
                  )
                : undefined;
            const input = {
                companyId,
                category: finalCategory,
                itemName: itemName.trim(),
                ...(categorySortOrder != null ? { categorySortOrder } : {}),
            };

            if (editItem && !isCopy) {
                await updateServiceProcedure({ id: editItem.id, ...input });
                await onSave(editItem);
            } else {
                const created = await createServiceProcedure({
                    ...input,
                    createdByUserId: currentUserId,
                    createdByUserName: currentUserName,
                });
                if (isCopy && editItem?.id && created?.id) {
                    const sourceLinks = await listServiceProcedureConsumablesByProcedureId(editItem.id, companyId);
                    await Promise.all(
                        sourceLinks.map((link, index) =>
                            createServiceProcedureConsumable({
                                companyId,
                                procedureId: created.id,
                                serviceItemId: link.serviceItemId,
                                quantity: link.quantity ?? null,
                                sortOrder: link.sortOrder ?? index,
                            }),
                        ),
                    );
                }
                await onSave(created);
            }
        } catch (err) {
            console.error('Error saving service procedure:', err);
            setErrors({ _form: 'Failed to save. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const title = isCopy
        ? `Copy ${editItem?.itemName?.trim() || 'procedure'}`
        : editItem
            ? 'Edit Procedure'
            : 'Add Procedure';

    const categoryField = (
        <div className="cr-field">
            <label className="cr-field-label" htmlFor="service-procedure-category">
                Category <span className="cr-required" aria-hidden="true">*</span>
            </label>
            <SearchSuggest
                options={categoryOptions}
                value={categoryValue}
                onChange={handleCategoryChange}
                placeholder="Type to search or add"
                newLabel="+ New Category"
                allowNew
                hasError={!!errors.category}
                ariaDescribedBy={errors.category ? 'service-procedure-category-error' : undefined}
                inputId="service-procedure-category"
            />
            {errors.category && (
                <div id="service-procedure-category-error" className="cr-field-error-text" role="alert">
                    {errors.category}
                </div>
            )}
        </div>
    );

    const procedureField = (
        <div className="cr-field">
            <label className="cr-field-label" htmlFor="service-procedure-name">
                Procedure Name <span className="cr-required" aria-hidden="true">*</span>
            </label>
            <InputField
                inputId="service-procedure-name"
                ariaLabel="Procedure Name"
                value={itemName}
                onChange={setItemName}
                placeholder="e.g. Grease Cone Crusher Main Bearings"
                autoFocus
                hasError={!!errors.itemName}
                ariaDescribedBy={errors.itemName ? 'service-procedure-name-error' : undefined}
            />
            {errors.itemName && (
                <div id="service-procedure-name-error" className="cr-field-error-text" role="alert">
                    {errors.itemName}
                </div>
            )}
        </div>
    );

    return (
        <CustomModal
            visible={isOpen}
            dismissOnBackdrop={false}
            onDismiss={() => {
                if (!saving) onClose();
            }}
            header={title}
            preferBodyFocus
            describedById={errors._form ? 'service-procedure-form-error' : undefined}
            footer={
                <div className="modal-footer-actions">
                    <Btn
                        variant="secondary-outlined"
                        buttonText="Cancel"
                        buttonOnClick={onClose}
                        buttonDisabled={saving}
                    />
                    <Btn
                        variant="primary"
                        buttonText={
                            saving
                                ? 'Saving...'
                                : editItem && !isCopy
                                  ? 'Update Procedure'
                                  : 'Save Procedure'
                        }
                        buttonOnClick={handleSubmit}
                        buttonDisabled={saving}
                    />
                </div>
            }
        >
            {errors._form && (
                <div id="service-procedure-form-error" className="cr-error-banner" role="alert">
                    {errors._form}
                </div>
            )}

            {procedureField}
            {categoryField}

            {isCopy && (
                <div className="cr-field-helper">
                    {linkedConsumableCount > 0
                        ? 'Linked consumables will be copied. The items themselves stay in Service Items.'
                        : 'No consumables linked yet.'}
                </div>
            )}
        </CustomModal>
    );
};
